/**
 * 学习页的纯函数（与 iOS Learn/*.swift 一致，单元测试见 learn.test.ts）：
 * 模块类型显示、内容列表文字、已布置排序 / 状态、可挂的时间段、自动截止时间、payload 合并。
 * 这里只引入 core / ui 的纯模块，方便在 node 环境下测试。
 */
import type { LocalCalendar } from "../../core/calendar";
import type { AgendaDay, AgendaItem, Assignment, Content, Folder, JSONObject, JSONValue, ScheduleEvent, Tag } from "../../core/models";
import { eventTimeMode, isContentItem, isTimedEvent } from "../../core/models";
import { activityInfo } from "../../activities/registry";
import { type FormField, type FormValues, mergeFormValues, sameJSON } from "../../activities/schema-form";
import { C, categoryStyle } from "../../ui/styles";

// ---------- 模块类型（名称、色块来自注册表 contracts/activities/bundle.json） ----------

/** 模块类型的中文名 */
export function activityName(type: string): string {
  return activityInfo(type).name;
}

/** 内容列表左侧色块上的短字：PDF / 写字 / 视频 / H5 */
export function typeBadge(type: string): { label: string; tint: string; ink: string } {
  const b = activityInfo(type).badge;
  return { label: b.short, tint: b.tint, ink: b.ink };
}

/** 文件夹配色，按顺序轮换 */
export function folderStyle(index: number): { tint: string; ink: string } {
  const after = categoryStyle("after_school");
  const course = categoryStyle("course");
  const other = categoryStyle("other");
  const list = [
    { tint: after.tint, ink: after.ink },
    { tint: course.tint, ink: course.ink },
    { tint: C.purpleSoft, ink: C.purple },
    { tint: C.accentSoft, ink: C.gold },
    { tint: other.tint, ink: other.ink },
  ];
  return list[((index % list.length) + list.length) % list.length]!;
}

// ---------- payload ----------

export function isObject(v: JSONValue | null | undefined): v is JSONObject {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** 内容的 payload（不是对象时当作空对象） */
export function payloadOf(c: Pick<Content, "payload">): JSONObject {
  return isObject(c.payload) ? c.payload : {};
}

export function str(v: JSONValue | undefined): string | null {
  return typeof v === "string" ? v : null;
}

export function int(v: JSONValue | undefined): number | null {
  return typeof v === "number" && Number.isFinite(v) ? Math.round(v) : null;
}

export function bool(v: JSONValue | undefined): boolean | null {
  return typeof v === "boolean" ? v : null;
}

/** 写字内容的字数 */
export function charCount(c: Pick<Content, "activityType" | "payload">): number | null {
  if (c.activityType !== "handwriting") return null;
  const list = payloadOf(c).characters;
  return Array.isArray(list) ? list.length : null;
}

/**
 * 合并 payload（与 iOS mergePayload 一致）：changes 里值为 null / undefined 的键删除，其它覆盖；未提到的键保留。
 */
export function mergePayload(existing: JSONValue | null | undefined, changes: Record<string, JSONValue | undefined>): JSONObject {
  const out: JSONObject = isObject(existing) ? { ...existing } : {};
  for (const [k, v] of Object.entries(changes)) {
    if (v === null || v === undefined) delete out[k];
    else out[k] = v;
  }
  return out;
}

/** 键排序后的 JSON（比较两个 payload 是否相同） */
export function stableStringify(v: JSONValue | undefined): string {
  if (v === undefined) return "undefined";
  if (Array.isArray(v)) return `[${v.map(stableStringify).join(",")}]`;
  if (isObject(v)) {
    return `{${Object.keys(v)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stableStringify(v[k])}`)
      .join(",")}}`;
  }
  return JSON.stringify(v);
}

export function jsonEqual(a: JSONValue | undefined, b: JSONValue | undefined): boolean {
  return stableStringify(a) === stableStringify(b);
}

// ---------- 布置时的设置（字段来自 Schema 的 x-assign，见 activities/schema-form.ts） ----------

/** 设置表单改的对象：原生模块是 payload，互动游戏是 payload.params */
export function settingsObject(content: Content): JSONObject {
  const p = payloadOf(content);
  if (content.activityType !== "web_activity") return p;
  return isObject(p.params) ? p.params : {};
}

/** 按设置生成新的 payload；没有改动返回 null */
export function modifiedPayload(content: Content, fields: FormField[], values: FormValues): JSONObject | null {
  if (!fields.length || !isObject(content.payload)) return null;
  const original = settingsObject(content);
  const merged = mergeFormValues(values, original, fields);
  if (sameJSON(merged, original)) return null;
  return content.activityType === "web_activity" ? { ...content.payload, params: merged } : merged;
}

// ---------- 内容列表 ----------

/** 内容行的副标题："#语文 #一年级上 · 6 个字 · 10 分钟 · 今天"；summary 是模块给的一句话（modules.tsx） */
export function contentMeta(c: Content, tags: Record<string, Tag>, folders: Record<string, Folder>, cal: LocalCalendar, summary: string | null = null): string {
  const parts: string[] = [];
  const tagNames = (c.tagIds ?? []).map((id) => tags[id]?.name).filter((n): n is string => !!n);
  if (tagNames.length) parts.push(tagNames.map((n) => `#${n}`).join(" "));
  else if (c.folderId && folders[c.folderId]) parts.push(folders[c.folderId]!.name);
  if (summary) parts.push(summary);
  if (c.estimatedMin != null) parts.push(`${c.estimatedMin} 分钟`);
  if (c.createdAt != null) parts.push(cal.relativeDay(cal.dateString(c.createdAt)));
  if (!parts.length) parts.push(activityName(c.activityType));
  return parts.join(" · ");
}

export function sortFolders(list: Folder[]): Folder[] {
  return [...list].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0) || a.name.localeCompare(b.name, "zh-CN"));
}

export function sortTags(list: Tag[]): Tag[] {
  return [...list].sort((a, b) => a.name.localeCompare(b.name, "zh-CN"));
}

/** 新添加的在前 */
export function newestFirst(list: Content[]): Content[] {
  return [...list].sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
}

// ---------- 已布置 ----------

export function isPending(a: Pick<Assignment, "status">): boolean {
  return a.status === "planned" || a.status === "in_progress";
}

function statusRank(a: Assignment): number {
  return a.status === "in_progress" ? 0 : a.status === "planned" ? 1 : 2;
}

/** 进行中 → 待完成（按日期）→ 其它（最近完成的在前），与 iOS AssignedList 一致 */
export function sortAssignments(list: Assignment[]): Assignment[] {
  return [...list].sort((a, b) => {
    const ra = statusRank(a);
    const rb = statusRank(b);
    if (ra !== rb) return ra - rb;
    if (ra < 2) return a.plannedDate < b.plannedDate ? -1 : a.plannedDate > b.plannedDate ? 1 : 0;
    return (b.completedAt ?? 0) - (a.completedAt ?? 0);
  });
}

/** 状态标签：完成 82分 / 已完成 / 进行中 33% / 跳过 / 待完成 */
export function statusLabel(a: Pick<Assignment, "status" | "progress" | "result">): { text: string; tint: string; ink: string } {
  switch (a.status) {
    case "completed": {
      const score = isObject(a.result ?? null) ? int((a.result as JSONObject).score) : null;
      return { text: score != null ? `完成 ${score}分` : "已完成", tint: C.successSoft, ink: categoryStyle("other").ink };
    }
    case "in_progress": {
      const p = isObject(a.progress ?? null) ? (int((a.progress as JSONObject).percent) ?? 0) : 0;
      return { text: `进行中 ${p}%`, tint: C.accentSoft, ink: C.gold };
    }
    case "skipped":
      return { text: "跳过", tint: C.chip, ink: C.ink };
    default:
      return { text: "待完成", tint: categoryStyle("course").tint, ink: categoryStyle("course").ink };
  }
}

// ---------- 布置：时间段与截止时间 ----------

export interface SlotRef {
  date: string;
  item: AgendaItem;
}

export function slotKey(s: SlotRef): string {
  return `${s.date}|${s.item.eventId}|${s.item.startAt}`;
}

/**
 * 可以挂学习的时间段（与 iOS AssignView.slots 一致）：还没结束、占时间（不是全天 / 学习任务 / 跨天中间的整天）、
 * 跨天日程只能挂在第一天那段（服务端只把学习安排放在开始那天）。
 */
export function assignableSlots(days: (AgendaDay | undefined)[], now: number): SlotRef[] {
  const out: SlotRef[] = [];
  for (const d of days) {
    if (!d) continue;
    for (const it of d.items) {
      if (it.endAt > now && isTimedEvent(it) && !isContentItem(it) && (it.dayIndex ?? 1) === 1) out.push({ date: d.date, item: it });
    }
  }
  return out;
}

/** 默认选第一个课后时间段，没有就选第一个 */
export function defaultSlot(slots: SlotRef[]): SlotRef | null {
  return slots.find((s) => s.item.category === "after_school") ?? slots[0] ?? null;
}

/** 自动截止时间：挂到时间段时为时间段结束时，指定日期时为当天 20:00（家庭时区） */
export function autoDueAt(cal: LocalCalendar, bySlot: boolean, slot: SlotRef | null, date: string): number | null {
  if (bySlot) return slot ? slot.item.endAt : null;
  return cal.ms(date, "20:00");
}

/** "今天" / "明天" / "周三" */
export function dayLabel(cal: LocalCalendar, date: string): string {
  const r = cal.relativeDay(date);
  return r === "今天" || r === "明天" ? r : cal.weekdayName(date);
}

/** 已布置行的时间说明："今天 · 课后作业 16:30" / "明天 · 20:00 前" */
export function assignmentWhen(a: Assignment, event: ScheduleEvent | null | undefined, cal: LocalCalendar): string {
  let when = cal.relativeDay(a.plannedDate);
  if (event) {
    const mode = eventTimeMode(event);
    // 自己的学习日程只写时间；放在别的日程里的（旧数据）带上日程名
    const own = event.action?.type === "content";
    const time = mode === "all_day" ? "全天" : mode === "due" ? `${event.startTime} 前` : event.startTime;
    when += own ? ` · ${time}${event.rrule ? " · 重复" : ""}` : ` · ${event.title} ${time}`;
  } else if (a.startTime) when += ` · ${a.startTime} 开始`;
  else if (a.dueAt != null) when += ` · ${cal.timeString(a.dueAt)} 前`;
  return when;
}

// ---------- 写字 ----------

/** 输入里的汉字，按出现顺序去重 */
export function uniqueHanzi(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const ch of text) {
    if (!/\p{Unified_Ideograph}/u.test(ch) || seen.has(ch)) continue;
    seen.add(ch);
    out.push(ch);
  }
  return out;
}

/** 组词之间用顿号、逗号、分号或空格分开 */
export function splitWords(text: string): string[] {
  return text
    .split(/[、,，;；\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export interface HanziEntry {
  char: string;
  pinyin: string;
  wordsText: string;
}

/** 写字 payload：characters（空的拼音 / 组词不写）+ mode，保留已有 payload 的其它字段 */
export function handwritingPayload(entries: HanziEntry[], free: boolean, existing?: JSONValue | null): JSONObject {
  const characters: JSONValue[] = entries.map((e) => {
    const d: JSONObject = { char: e.char };
    const py = e.pinyin.trim();
    if (py) d.pinyin = py;
    const words = splitWords(e.wordsText);
    if (words.length) d.words = words;
    return d;
  });
  return mergePayload(existing, { characters, mode: free ? "free" : "trace" });
}
